import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import './index.css';
import { useMapStore } from './store/useMapStore';
import Sidebar from './components/layout/Sidebar';
import SearchBar from './components/layout/SearchBar';
import ResultContainer from './components/layout/ResultContainer';
const ReportModal = React.lazy(() => import('./components/ReportModal'));

const LegalModal = React.lazy(() => import('./components/LegalModal'));

import { useTranslation } from './i18n/translations';
import ErrorBoundary from './components/ErrorBoundary';
const HealthFiltersPanel = React.lazy(() => import('./features/health/HealthFiltersPanel').then(m => ({ default: m.HealthFiltersPanel })));

const HealthAreaPrompt = React.lazy(() => import('./features/health/HealthAreaPrompt').then(m => ({ default: m.HealthAreaPrompt })));

import LocatingOverlay from './components/LocatingOverlay';
const TutorialGuide = React.lazy(() => import('./features/tutorial/TutorialGuide'));

import SEO from './components/layout/SEO';
const PWAUpdater = React.lazy(() => import('./components/PWAUpdater'));

import { RouteManager } from './components/routing/RouteManager';
import MapSkeleton from './components/layout/MapSkeleton';
import SchemaData from './components/SchemaData';
import { useLocation } from 'react-router-dom';
const NetworkErrorOverlay = React.lazy(() => import('./components/NetworkErrorOverlay'));

const PostalLegendPanel = React.lazy(() => import('./features/postal/PostalFiltersPanel').then(m => ({ default: m.PostalLegendPanel })));
const VeterinaryFiltersPanel = React.lazy(() => import('./features/veterinary/components/VeterinaryFiltersPanel').then(m => ({ default: m.VeterinaryFiltersPanel })));
const FuelFiltersPanel = React.lazy(() => import('./features/fuel/FuelFiltersPanel').then(m => ({ default: m.FuelFiltersPanel })));

import { trackEvent } from './lib/firebase';
import { useElectionResults } from './hooks/useElectionResults';

const ElectionDashboard = React.lazy(() => import('./features/election/ElectionDashboard'));
const ElectionDisclaimer = React.lazy(() => import('./features/election/ElectionDisclaimer'));

const AudioPlayer = React.lazy(() => import('./components/AudioPlayer').then(m => ({ default: m.AudioPlayer })));

const GisMap = React.lazy(() => import('./features/map/GisMap'));
const ModuleGuidePage = React.lazy(() => import('./features/guides/ModuleGuidePage'));

function App() {
  const [shouldLoadMap, setShouldLoadMap] = React.useState(false);
  const hasAppliedInitialSidebarRule = React.useRef(false);
  const { t } = useTranslation();
  const theme = useMapStore(state => state.theme);
  const activeLayer = useMapStore(state => state.activeLayer);
  const setSidebarOpen = useMapStore(state => state.setSidebarOpen);
  const setHealthFilters = useMapStore(state => state.setHealthFilters);
  const activeDistrict = useMapStore(state => state.activeDistrict);
  const language = useMapStore(state => state.language);
  const setLanguage = useMapStore(state => state.setLanguage);
  const selectedHealthFacility = useMapStore(state => state.selectedHealthFacility);
  const selectedVeterinaryFacility = useMapStore(state => state.selectedVeterinaryFacility);
  const selectedFuelStation = useMapStore(state => state.selectedFuelStation);
  const fuelSummary = useMapStore(state => state.fuelSummary);
  const healthSummary = useMapStore(state => state.healthSummary);
  const searchResult = useMapStore(state => state.searchResult);
  const isResultMinimized = useMapStore(state => state.isResultMinimized);
  const location = useLocation();
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const isGuideRoute = location.pathname === '/guides' || location.pathname.startsWith('/guides/');

  useElectionResults();

  // Sync store language state with ?lang=ta URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const lang = params.get('lang');
    if (lang === 'ta') {
      if (language !== 'ta') setLanguage('ta');
    } else if (lang === 'en') {
      if (language !== 'en') setLanguage('en');
    }
  }, [location.search, language, setLanguage]);

  // Collapse sidebar on initial deep-link entry (e.g. /pds/625006), but only once.
  useEffect(() => {
    if (hasAppliedInitialSidebarRule.current) return;
    hasAppliedInitialSidebarRule.current = true;

    const pathParts = location.pathname.split('/').filter(Boolean);
    const isDeepLink = pathParts.length >= 2;
    if (isDeepLink) setSidebarOpen(false);
  }, [location.pathname, setSidebarOpen]);

  // Track Page Views
  useEffect(() => {
    trackEvent('page_view', {
      page_path: location.pathname + location.search,
      active_layer: activeLayer,
      active_district: activeDistrict || 'statewide'
    });
  }, [location, activeLayer, activeDistrict]);


  // Defer Map Loading slightly to prioritize FCP, but not too much to avoid hurting LCP
  useEffect(() => {
    // A small delay lets the skeleton render instantly, then we immediately queue the map.
    // We removed the 2500ms delay as it artificially inflated the LCP score on mobile.
    const delay = 100;
    
    const timer = setTimeout(() => {
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(() => setShouldLoadMap(true), { timeout: 1000 });
      } else {
        setShouldLoadMap(true);
      }
    }, delay); 
    return () => clearTimeout(timer);
  }, []);



  // Sync theme with body class
  useEffect(() => {
    document.body.className = `${theme}-mode`;
  }, [theme]);

  return (
    <div className={`app-container ${theme}-mode lang-${language}`}>
      {!isGuideRoute && (
        <Suspense fallback={null}>
          <TutorialGuide />
        </Suspense>
      )}

      <a href="#main-content" className="skip-link">{t('SKIP_TO_RESULTS')}</a>
      <SchemaData />
      {!isGuideRoute && <SEO />}


      <Routes>

        <Route path="/guides" element={<React.Fragment />} />
        <Route path="/guides/:guideLayer" element={<React.Fragment />} />
        <Route path="/:layer/:district?" element={<RouteManager />} />
        <Route path="/" element={<Navigate to="/pincode" replace />} />
      </Routes>

      <Sidebar />
      
      <main className={`main-content ${isGuideRoute ? 'main-content-guides' : ''}`} role="main" id="main-content" tabIndex={-1}>
        {isGuideRoute ? (
          <ErrorBoundary>
            <Suspense fallback={<MapSkeleton />}>
              <ModuleGuidePage />
            </Suspense>
          </ErrorBoundary>
        ) : (
          <>
            <ErrorBoundary>
              <Suspense fallback={<MapSkeleton />}>
                {shouldLoadMap ? <GisMap /> : <MapSkeleton />}
              </Suspense>
            </ErrorBoundary>

            <SearchBar />
            <Suspense fallback={null}>
              <NetworkErrorOverlay />

              <LocatingOverlay />
              <HealthAreaPrompt />
              <ReportModal />
              <LegalModal />

              {activeLayer === 'HEALTH' && (!isMobile || isResultMinimized || (!selectedHealthFacility && !healthSummary && !searchResult)) && (
                <div className="health-filters-floating">
                  <HealthFiltersPanel 
                    onFilterChange={(filters) => {
                      setHealthFilters(filters);
                    }}
                  />
                </div>
              )}

              {activeLayer === 'VETERINARY' && (!isMobile || isResultMinimized || (!selectedVeterinaryFacility && !searchResult)) && (
                <div className="veterinary-filters-floating">
                  <VeterinaryFiltersPanel />
                </div>
              )}

              {activeLayer === 'FUEL' && (!isMobile || isResultMinimized || (!selectedFuelStation && !fuelSummary && !searchResult)) && (
                <div className="fuel-filters-floating">
                  <FuelFiltersPanel />
                </div>
              )}
              
              {activeLayer === 'PINCODE' && <PostalLegendPanel />}

              <ElectionDashboard />
              <ElectionDisclaimer />

              <ResultContainer />
              <AudioPlayer />
              <PWAUpdater />
            </Suspense>
          </>
        )}
      </main>
    </div>
  );
}

export default App;
